import { post, setToken, setUser, clearToken, clearUser, setRefreshToken, clearRefreshToken } from './api';
import { API_ENDPOINTS } from './config';
import { getDeviceFingerprint } from '../utils/fingerprint';

// 发送验证码（带设备指纹）
export async function sendVerificationCode(email: string) {
  try {
    const deviceInfo = await getDeviceFingerprint();
    const response = await post(API_ENDPOINTS.AUTH.SEND_CODE, {
      email,
      screenResolution: deviceInfo.screenResolution,
      timezone: deviceInfo.timezone,
      canvasFingerprint: deviceInfo.canvasFingerprint,
      webglFingerprint: deviceInfo.webglFingerprint
    });
    return response.data;
  } catch (e) {
    // 如果获取设备指纹失败，仍然发送请求（后端会使用简化模式）
    const response = await post(API_ENDPOINTS.AUTH.SEND_CODE, { email });
    return response.data;
  }
}

// 用户注册
export async function register(data: {
  email: string;
  password: string;
  username: string;
  verificationCode: string;
  inviteCode?: string;
}) {
  // 采集设备指纹，随注册请求一起发送
  let deviceFingerprint: string | undefined;
  let deviceInfo: Record<string, string> | undefined;
  try {
    const fp = await getDeviceFingerprint();
    deviceFingerprint = fp.fingerprint;
    deviceInfo = {
      screenResolution: fp.screenResolution,
      timezone: fp.timezone,
      language: fp.language,
      platform: fp.platform,
      canvas: fp.canvasFingerprint,
      webgl: fp.webglFingerprint,
    };
  } catch (e) {
    // 采集失败不影响注册
  }

  const res = await post<any>(
    API_ENDPOINTS.AUTH.REGISTER,
    { ...data, deviceFingerprint, deviceInfo }
  );
  // 注册成功后，自动登录
  if (res.data) {
    const loginRes = await login({ account: data.email, password: data.password });
    return loginRes;
  }
  return res.data;
}

// 用户登录（密码方式，支持邮箱/手机号/用户名）
export async function login(data: { account: string; password: string }) {
  const res = await post<{ accessToken: string; refreshToken: string; userId: number; email: string; phone: string; username: string }>(
    API_ENDPOINTS.AUTH.LOGIN,
    data
  );
  if (res.data?.accessToken) {
    setToken(res.data.accessToken);
    if (res.data.refreshToken) setRefreshToken(res.data.refreshToken);
    setUser(res.data);
  }
  return res.data;
}

// 用户登录（验证码方式）
export async function loginWithCode(data: { email: string; verificationCode: string }) {
  const res = await post<{ accessToken: string; refreshToken: string; userId: number; email: string; username: string }>(
    '/user/login/verifycode',
    data
  );
  if (res.data?.accessToken) {
    setToken(res.data.accessToken);
    if (res.data.refreshToken) setRefreshToken(res.data.refreshToken);
    setUser(res.data);
  }
  return res.data;
}

// 重置密码
export async function resetPassword(data: { email: string; verificationCode: string; newPassword: string }) {
  const response = await post('/user/reset-password', data);
  return response.data;
}

// 发送短信验证码
export async function sendSmsCode(phone: string, scene: string) {
  const response = await post('/user/send-sms-code', { phone, scene });
  return response.data;
}

// 手机号登录
export async function loginWithPhone(data: { phone: string; code: string }) {
  const res = await post<{ accessToken: string; refreshToken: string; userId: number; email: string; username: string }>(
    '/user/login/phone',
    data
  );
  if (res.data?.accessToken) {
    setToken(res.data.accessToken);
    if (res.data.refreshToken) setRefreshToken(res.data.refreshToken);
    setUser(res.data);
  }
  return res.data;
}

// 绑定手机号
export async function bindPhone(data: { phone: string; code: string }) {
  const response = await post('/user/bind-phone', data);
  return response.data;
}

// 绑定邮箱
export async function bindEmail(data: { email: string; code: string }) {
  const response = await post('/user/bind-email', data);
  return response.data;
}

// 手机号重置密码
export async function resetPasswordByPhone(data: { phone: string; code: string; newPassword: string }) {
  const response = await post('/user/reset-password/phone', data);
  return response.data;
}

// 手机号注册
export async function registerWithPhone(data: {
  phone: string;
  code: string;
  password: string;
  username?: string;
  occupation?: string;
  inviteCode?: string;
}) {
  // 采集设备指纹，随注册请求一起发送
  let deviceFingerprint: string | undefined;
  let deviceInfo: Record<string, string> | undefined;
  try {
    const fp = await getDeviceFingerprint();
    deviceFingerprint = fp.fingerprint;
    deviceInfo = {
      screenResolution: fp.screenResolution,
      timezone: fp.timezone,
      language: fp.language,
      platform: fp.platform,
      canvas: fp.canvasFingerprint,
      webgl: fp.webglFingerprint,
    };
  } catch (e) {
    // 采集失败不影响注册
  }

  const res = await post<any>('/user/register/phone', { ...data, deviceFingerprint, deviceInfo });
  // 注册成功后，尝试自动登录，如果登录失败则返回注册结果（让用户手动登录）
  if (res.data) {
    try {
      const loginRes = await login({ account: data.phone, password: data.password });
      return loginRes;
    } catch (loginError) {
      // 登录失败，但注册已成功，返回注册结果
      console.warn('自动登录失败，请手动登录:', loginError);
      return res.data;
    }
  }
  return res.data;
}

// 登出
export function logout() {
  clearToken();
  clearRefreshToken();
  clearUser();
}
